import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const location = useLocation();
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const servicesDropdown = [
    { name: 'Commercial Appraisals', href: '/services#commercial' },
    { name: 'Residential Appraisals', href: '/services#residential' },
    { name: 'Estate Valuations', href: '/services#estate' },
    { name: 'Divorce Appraisals', href: '/services#divorce' },
    { name: 'Tax Appeals', href: '/services#tax-litigation' },
    { name: 'Litigation Support', href: '/services#tax-litigation' },
    { name: 'Bank & Mortgage Financing Appraisals', href: '/services#bank-mortgage' },
    { name: 'Market Rent Studies & Feasibility Analysis', href: '/services#market-rent' }
  ];

  const aboutDropdown = [
    { name: 'About the Company', href: '/about' },
    { name: 'About the Founder', href: '/about#founder' }
  ];

  const processDropdown = [
    { name: 'Our Process Overview', href: '/process' },
    { name: 'Step 1: Request & Consultation', href: '/process#step-1' },
    { name: 'Step 2: Property Inspection', href: '/process#step-2' },
    { name: 'Step 3: Research & Market Analysis', href: '/process#step-3' },
    { name: 'Step 4: Valuation & Report Preparation', href: '/process#step-4' },
    { name: 'Step 5: Review & Delivery', href: '/process#step-5' }
  ];

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services', dropdown: servicesDropdown },
    { name: 'About', href: '/about', dropdown: aboutDropdown },
    { name: 'Process', href: '/process', dropdown: processDropdown },
    { name: 'Contact', href: '/contact' }
  ];

  const handleMouseEnter = (name: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleDropdownClick = (e: React.MouseEvent, href: string) => {
    setActiveDropdown(null);
    setMobileDropdown(null);
    setIsMenuOpen(false);
    
    // Handle hash navigation
    if (href.includes('#')) {
      const [path, hash] = href.split('#');
      if (location.pathname === path) {
        e.preventDefault();
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  useEffect(() => {
    // Handle hash scrolling after navigation
    if (location.hash) {
      setTimeout(() => {
        const element = document.getElementById(location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <header className="bg-black-900 shadow-lg sticky top-0 z-50">
      <nav className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-32">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-6">
              <img 
                src="https://static.readdy.ai/image/0c5a07476bd7a7109b05d9670ab2ad74/c9f1fabad792b1ff7b704ac804cb90ec.jpeg"
                alt="Virtus Realty Appraisal Services LLC Logo"
                className="h-32 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="text-2xl font-bold text-white">
                  Virtus Realty Appraisal Services LLC
                </h1>
                <p className="text-base text-yellow-400">Certified General Appraisers</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && handleMouseEnter(item.name)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={item.href}
                  className={`text-base font-medium font-modern transition-colors cursor-pointer flex items-center ${
                    location.pathname === item.href
                      ? 'text-yellow-400'
                      : 'text-white hover:text-yellow-400'
                  }`}
                >
                  {item.name}
                  {item.dropdown && (
                    <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`}></i>
                  )}
                </Link>
                
                {/* Dropdown Menu */}
                {item.dropdown && (
                  <div 
                    className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 transition-all duration-200 ${
                      activeDropdown === item.name 
                        ? 'opacity-100 visible translate-y-0' 
                        : 'opacity-0 invisible -translate-y-2'
                    }`}
                  >
                    {item.dropdown.map((dropItem, index) => (
                      <Link
                        key={index}
                        to={dropItem.href}
                        onClick={(e) => handleDropdownClick(e, dropItem.href)}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-600 transition-colors cursor-pointer font-modern"
                      >
                        {dropItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact">
              <Button size="sm" className="gradient-gold text-black-900 hover:shadow-gold font-semibold">
                Get Quote
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-yellow-400 cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-yellow-600">
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.dropdown ? (
                    <>
                      <button
                        onClick={() => setMobileDropdown(mobileDropdown === item.name ? null : item.name)}
                        className={`w-full text-left text-base font-medium font-modern cursor-pointer flex items-center justify-between py-2 ${
                          location.pathname === item.href
                            ? 'text-yellow-400'
                            : 'text-white hover:text-yellow-400'
                        }`}
                      >
                        {item.name}
                        <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileDropdown === item.name ? 'rotate-180' : ''}`}></i>
                      </button>
                      
                      {/* Mobile Dropdown */}
                      <div className={`overflow-hidden transition-all duration-300 ${
                        mobileDropdown === item.name ? 'max-h-96' : 'max-h-0'
                      }`}>
                        <div className="pl-4 py-2 space-y-2 border-l-2 border-yellow-500 ml-2">
                          {item.dropdown.map((dropItem, index) => (
                            <Link
                              key={index}
                              to={dropItem.href}
                              onClick={(e) => handleDropdownClick(e, dropItem.href)}
                              className="block text-sm text-gray-300 hover:text-yellow-400 py-1.5 cursor-pointer font-modern"
                            >
                              {dropItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block text-base font-medium font-modern cursor-pointer py-2 ${
                        location.pathname === item.href
                          ? 'text-yellow-400'
                          : 'text-white hover:text-yellow-400'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                <Button size="sm" className="gradient-gold text-black-900 hover:shadow-gold font-semibold w-fit mt-2">
                  Get Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
